import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./styles/index.css";

import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { AccessGate } from "./components/AccessGate";
import { ScrollToTop } from "./components/ScrollToTop";
import { hasAccess } from "./access/access";

/* Pages */
import { Home } from "./pages/Home";
import { TapMode } from "./pages/TapMode";
import { Issues } from "./pages/Issues";
import { IssueDetail } from "./pages/IssueDetail";
import { Cards } from "./pages/Cards";
import { Diagnosis } from "./pages/Diagnosis";
import { Maintenance } from "./pages/Maintenance";
import { Bookmarks } from "./pages/Bookmarks";
import { Notes } from "./pages/Notes";
import { Admin } from "./pages/Admin";
import { Glossary } from "./pages/Glossary";
import { CostCalculator } from "./pages/CostCalculator";
import BusinessBuilderPage from "./pages/BusinessBuilderPage";
import BusinessGuideDetail from "./pages/BusinessGuideDetail";
import ArtworkPrepActions from "./pages/ArtworkPrepActions";
import KnowledgeTest from "./pages/KnowledgeTest";
import DiagnosticAssistantPage from "./pages/DiagnosticAssistantPage";
import MockupBuilderPage from "./pages/MockupBuilderPage";
import GangsheetBuilderPage from "./pages/GangsheetBuilderPage";
import WhiteLayerToolPage from "./pages/WhiteLayerToolPage";
import ColorKnockoutStudioPage from "./pages/ColorKnockoutStudioPage";
import SpotWizardPage from "./pages/SpotWizardPage";

/* Defaults */
import Vendors from "./pages/Vendors";
import Videos from "./pages/Videos";

/* How-To */
import HowTo from "./pages/howto";
import HowToDetail from "./pages/howtodetail";

/* SEO */
import FixHub from "./pages/fix/FixHub";
import DtfPrintNotSticking from "./pages/fix/DtfPrintNotSticking";
import DtfWhiteInkNotPrinting from "./pages/fix/DtfWhiteInkNotPrinting";
import DtfBandingLines from "./pages/fix/DtfBandingLines";
import DtfPowderNotCuring from "./pages/fix/DtfPowderNotCuring";
import DtfPeelingAfterWash from "./pages/fix/DtfPeelingAfterWash";
import DtfColorsDull from "./pages/fix/DtfColorsDull";
import DtfInkSmearing from "./pages/fix/DtfInkSmearing";
import DtfGhosting from "./pages/fix/DtfGhosting";
import DtfFilmNotReleasing from "./pages/fix/DtfFilmNotReleasing";
import DtfCrackingAfterPressing from "./pages/fix/DtfCrackingAfterPressing";
import DtfBlurryPrints from "./pages/fix/DtfBlurryPrints";
import DtfStaticIssues from "./pages/fix/DtfStaticIssues";
import DtfUnevenPowder from "./pages/fix/DtfUnevenPowder";
import DtfUnderbaseMisalignment from "./pages/fix/DtfUnderbaseMisalignment";
import DtfAdhesionIssues from "./pages/fix/DtfAdhesionIssues";

/* Debug */
import { logHowTos } from "./debug/howtodebug";

if (import.meta.env.DEV) {
  try {
    logHowTos();
  } catch (e) {
    console.warn("logHowTos failed:", e);
  }
}

function HomeRoute() {
  if (hasAccess()) return <Home />;

  return (
    <AccessGate>
      <Home />
    </AccessGate>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return <AccessGate>{children}</AccessGate>;
}

function AppShell() {
  const location = useLocation();
  const isTapMode = location.pathname === "/tap";

  return (
    <div className="relative flex min-h-screen flex-col bg-dtf-dark">
      <div className="graffiti-bg fixed inset-0 -z-30" />

      <div
        className="pointer-events-none fixed inset-0 -z-20"
        style={{
          backgroundImage: "url('/apex-graffiti-logo.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "min(1000px, 90vw) auto",
          opacity: 0.12,
        }}
      />

      <div className="pointer-events-none fixed inset-0 -z-10 bg-[rgba(5,8,15,0.35)]" />

      {!isTapMode && <Header />}

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomeRoute />} />

          {/* SEO */}
          <Route path="/fix" element={<FixHub />} />
          <Route path="/fix/dtf-print-not-sticking" element={<DtfPrintNotSticking />} />
          <Route path="/fix/dtf-white-ink-not-printing" element={<DtfWhiteInkNotPrinting />} />
          <Route path="/fix/dtf-banding-lines" element={<DtfBandingLines />} />
          <Route path="/fix/dtf-powder-not-curing" element={<DtfPowderNotCuring />} />
          <Route path="/fix/dtf-peeling-after-wash" element={<DtfPeelingAfterWash />} />
          <Route path="/fix/dtf-colors-dull" element={<DtfColorsDull />} />
          <Route path="/fix/dtf-ink-smearing" element={<DtfInkSmearing />} />
          <Route path="/fix/dtf-ghosting" element={<DtfGhosting />} />
          <Route path="/fix/dtf-film-not-releasing" element={<DtfFilmNotReleasing />} />
          <Route path="/fix/dtf-cracking-after-pressing" element={<DtfCrackingAfterPressing />} />
          <Route path="/fix/dtf-blurry-prints" element={<DtfBlurryPrints />} />
          <Route path="/fix/dtf-static-issues" element={<DtfStaticIssues />} />
          <Route path="/fix/dtf-uneven-powder" element={<DtfUnevenPowder />} />
          <Route path="/fix/dtf-underbase-misalignment" element={<DtfUnderbaseMisalignment />} />
          <Route path="/fix/dtf-adhesion-issues" element={<DtfAdhesionIssues />} />

          {/* Protected app pages */}
          <Route path="/tap" element={<ProtectedRoute><TapMode /></ProtectedRoute>} />
          <Route path="/issues" element={<ProtectedRoute><Issues /></ProtectedRoute>} />
          <Route path="/issues/:id" element={<ProtectedRoute><IssueDetail /></ProtectedRoute>} />
          <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
          <Route path="/diagnosis" element={<ProtectedRoute><Diagnosis /></ProtectedRoute>} />
          <Route path="/smart-scan" element={<ProtectedRoute><DiagnosticAssistantPage /></ProtectedRoute>} />
          <Route path="/maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
          <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
          <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/glossary" element={<ProtectedRoute><Glossary /></ProtectedRoute>} />
          <Route path="/calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
          <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
          <Route path="/videos" element={<ProtectedRoute><Videos /></ProtectedRoute>} />
          <Route path="/howto" element={<ProtectedRoute><HowTo /></ProtectedRoute>} />
          <Route path="/howto/:id" element={<ProtectedRoute><HowToDetail /></ProtectedRoute>} />
          <Route path="/artwork-prep-actions" element={<ProtectedRoute><ArtworkPrepActions /></ProtectedRoute>} />
          <Route path="/mockup-builder" element={<ProtectedRoute><MockupBuilderPage /></ProtectedRoute>} />
          <Route path="/gangsheet-builder" element={<ProtectedRoute><GangsheetBuilderPage /></ProtectedRoute>} />
          <Route path="/white-layer-tool" element={<ProtectedRoute><WhiteLayerToolPage /></ProtectedRoute>} />
          <Route path="/color-knockout" element={<ProtectedRoute><ColorKnockoutStudioPage /></ProtectedRoute>} />
          <Route path="/spot-wizard" element={<ProtectedRoute><SpotWizardPage /></ProtectedRoute>} />
          <Route path="/knowledge-test" element={<ProtectedRoute><KnowledgeTest /></ProtectedRoute>} />
          <Route path="/business-builder" element={<ProtectedRoute><BusinessBuilderPage /></ProtectedRoute>} />
          <Route path="/business-builder/:id" element={<ProtectedRoute><BusinessGuideDetail /></ProtectedRoute>} />

          <Route path="*" element={<HomeRoute />} />
        </Routes>
      </main>

      {!isTapMode && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppShell />
    </BrowserRouter>
  );
}